import Head from "next/head";
import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import MerakiSDK, {
  ANALYTICS_EVENTS,
} from "@urbanpiper-engineering/meraki-sdk";
import { Exceptions } from "@urbanpiper-engineering/meraki-sdk";

import { getStaticProps as internalProps } from "../../hooks/server.props";

import MenuPanel from "@urbanpiper-engineering/meraki-components/dist/Organisms/MenuPanel";

import styles from "./menu.module.scss";
import { CartState } from "@urbanpiper-engineering/meraki-sdk/dist/esm/cart/states/cart.state";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";

import { StoreState } from "@urbanpiper-engineering/meraki-sdk/dist/esm/stores/states/store.state";
import configSelector from "../../external/configSelector";
import { PubSub, EVENTS } from "../../utils/listeners";
import pageSelector from "../../external/pageSelector";
import { useTranslation } from "react-i18next";
import { onItemUpdate } from "../../hooks/cart.util";
import { onItemCustomization } from "../../hooks/cart.util";
import { useIsMobile } from "../../hooks/is_mobile.hook";

import type {
  ImageType,
  FulfillmentMode,
} from "@urbanpiper-engineering/meraki-sdk/dist/esm/graphql/generated";
import { LazyImage } from "../../components/lazy-image";
import { Typography } from "@urbanpiper-engineering/meraki-components";
import {
  CarouselMobile,
  CarouselDesktop,
  CarouselMultiShimmer,
  CarouselWideShimmer,
} from "@urbanpiper-engineering/meraki-components/dist/Cells/Carousel";
import { defaultPlaceholderBanners } from "../../constants/default-banners.constants";
import LocationSearch from "../../assets/images/location-search";
// const CarouselDesktop = dynamic(
//   () =>
//     import(
//       "@urbanpiper-engineering/meraki-components/dist/Cells/Carousel"
//     ).then((mod) => mod.CarouselDesktop),
//   {
//     ssr: false,
//     loading: () => <div className={styles.carouselSuspense} />,
//   }
// );

const FloatingStack = dynamic(
  () =>
    import(
      "@urbanpiper-engineering/meraki-components/dist/Atoms/FloatingStack"
    ),
  { ssr: false },
);

const FloatingCart = dynamic(
  () =>
    import("@urbanpiper-engineering/meraki-components/dist/Cells/FloatingCart"),
  { ssr: false },
);

const FloatingStatus = dynamic(
  () =>
    import(
      "@urbanpiper-engineering/meraki-components/dist/Molecules/FloatingStatus"
    ),
  { ssr: false },
);

const getFullCatalogue = async (
  allCategories: any[],
  selectedSortOptions: any,
  selectedfilterOptions: any,
) => {
  const itemsMapTemp: any = {};
  allCategories.forEach((category: any) => {
    if (category?.sub_categories?.length) {
      category.sub_categories.forEach((subCategory: any) => {
        itemsMapTemp[subCategory.id] = [];
      });
    } else {
      itemsMapTemp[category.id] = [];
    }
  });

  const categoryIds = Object.keys(itemsMapTemp).map((id) => Number(id));

  const categoryItems = await MerakiSDK.getCategoryItems({
    categoryIds: categoryIds,
    sort_by: selectedSortOptions,
    filter_by: selectedfilterOptions,
  });

  const categoryItemMap = categoryItems?.reduce((acc: any, category: any) => {
    acc[category.id] = category.items?.map((item: any) => ({
      ...item,
      isAlcoholic: MerakiSDK.isItemAlcoholic(item),
    }));
    return acc;
  }, {});

  return categoryItemMap;
};

const getBanners = async (defaultBanners: boolean) => {
  const banners = await MerakiSDK.getBannerImages({
    type: ["web_banner", "mobile_web_banner"] as ImageType[],
  });

  const bannersToShow = banners.images.length
    ? banners
    : defaultBanners
      ? defaultPlaceholderBanners
      : { images: [] };

  const { webBanner, mobileBanner } = bannersToShow.images.reduce(
    (acc: any, banner: any) => {
      const imgType: string[] = banner.img_type
        .split(",")
        .map((type: string) => type.trim());
      if (imgType?.includes("web_banner")) acc.webBanner.push(banner);
      if (imgType?.includes("mobile_web_banner")) acc.mobileBanner.push(banner);
      return acc;
    },
    { webBanner: [], mobileBanner: [] },
  );

  return { webBanner, mobileBanner };
};

export async function getStaticProps(props: any) {
  const { props: initialProps } = await internalProps(props);

  const seoConfig =
    configSelector.getPageSEO({ name: "menu", locale: props.locale }) || {};

  const currentTheme = configSelector.getThemeName();

  const {
    likeButton,
    recommendedItems,
    showFoodType,
    showItemSort,
    showItemFilter,
    themeName = currentTheme,
    bannerLayout = currentTheme === "luna" ? "multi" : "single",
    defaultBanners = false,
    mandatoryLocationSelection = false,
  } = pageSelector.getMenuPage().customizations;

  const { webBanner, mobileBanner } = await getBanners(defaultBanners);

  return {
    props: {
      ...initialProps,
      seoConfig,
      likeButton,
      recommendedItems,
      showFoodType,
      showItemSort,
      showItemFilter,
      webBanner,
      mobileBanner,
      themeName,
      bannerLayout,
      defaultBanners,
      mandatoryLocationSelection,
    },
  };
}

function MenuPage({
  categoryId,
  seoConfig,
  businessInfo,
  likeButton,
  recommendedItems,
  showFoodType,
  showItemSort,
  showItemFilter,
  webBanner,
  mobileBanner,
  bannerLayout,
  themeName,
  defaultBanners,
  mandatoryLocationSelection,
}: any) {
  const initialMount = useRef(true);
  const isMobile = useIsMobile();
  const [webBannerForCarousel, setWebBanner] = useState<any>(webBanner);
  const [mobileBannerForCarousel, setMobileBanner] =
    useState<any>(mobileBanner);
  const _webBanner = webBannerForCarousel?.length
    ? webBannerForCarousel
    : webBanner;
  const _mobileBanner = mobileBannerForCarousel?.length
    ? mobileBannerForCarousel
    : mobileBanner;

  const router = useRouter();
  const { t } = useTranslation();
  const useCartState = MerakiSDK.getCartState().hook();
  const useStoreState = MerakiSDK.getStoreState().hook();
  const useUserState = MerakiSDK.getUserState().hook();

  const [cartItems, totalItems, subTotal] = useCartState(
    (state: typeof CartState) => [
      state.cart?.items ?? [],
      state.cart?.item_count,
      state.cart?.sub_total,
    ],
  );

  const selectedStore = useStoreState(
    (state: typeof StoreState) => state.selectedStore,
  );

  const [itemsMap, setItemsMap] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const language = useUserState((state: any) => state.language);
  const isUserAuthenticated = MerakiSDK.isUserAuthenticated();
  const [sortFilter, setSortFilter] = useState<any>({});
  const [activeOrder, setActiveOrder] = useState<any>(null);
  const [selectedfilterOptions, setSelectedfilterOptions] = useState<any>([]);
  const [selectedSortOptions, setSelectedSortOptions] = useState<any>(null);
  const [filterSortIndicatorData, setFilterSortIndicatorData] = useState<{
    showFilterIndicator: boolean;
    showSortIndicator: boolean;
  }>({ showFilterIndicator: false, showSortIndicator: false });
  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(true);

  const onFilterByChange = (checked: any, id: any) => {
    if (checked && !selectedfilterOptions.includes(id)) {
      setSelectedfilterOptions([...selectedfilterOptions, id]);
    } else if (selectedfilterOptions.includes(id)) {
      setSelectedfilterOptions(
        selectedfilterOptions.filter((filterId: any) => filterId != id),
      );
    }
  };

  const onSortByChange = (id: any) => {
    setSelectedSortOptions(sortFilter.sort_by?.[id]);
  };

  const onFilterByCallback = (
    selectedSortOptions: any,
    selectedfilterOptions: any,
    isHeaderEvent = false,
  ) => {
    if (!isHeaderEvent) {
      setSelectedSortOptions(selectedSortOptions);
      setSelectedfilterOptions(selectedfilterOptions);
    }
    repopulateCategories(
      selectedSortOptions,
      selectedfilterOptions,
      isHeaderEvent,
    );

    try {
      if (selectedfilterOptions?.length) {
        const _selectedfilterOptions = [...selectedfilterOptions].map(Number);
        let appliedFilters: string[] = [];
        let filters = sortFilter?.filters || [];
        filters.map((filter: any) => {
          filter?.options?.forEach(
            ({ id, title }: { id: number; title: string }) => {
              if (_selectedfilterOptions.includes(id)) {
                appliedFilters.push(title);
              }
            },
          );
        });

        MerakiSDK.analyticsPublish(ANALYTICS_EVENTS.FILTER_APPLIED, {
          filter_by: appliedFilters.join(", "),
        });
      }
    } catch (error) {
      console.error(
        "AnalyticsPublish: ",
        ANALYTICS_EVENTS.FILTER_APPLIED,
        error,
      );
    }

    try {
      if (selectedSortOptions) {
        MerakiSDK.analyticsPublish(ANALYTICS_EVENTS.SORT_APPLIED, {
          sort_by: selectedSortOptions,
        });
      }
    } catch (error) {
      console.error("AnalyticsPublish: ", ANALYTICS_EVENTS.SORT_APPLIED, error);
    }
  };

  const onFilterClearCallback = useCallback(() => {
    setSelectedfilterOptions([]);
    onFilterByCallback(selectedSortOptions, []);
    setFilterSortIndicatorData({
      showFilterIndicator: false,
      showSortIndicator: selectedSortOptions?.length > 0,
    });
  }, [selectedSortOptions]);

  const onSortClearCallback = useCallback(() => {
    setSelectedSortOptions(null);
    onFilterByCallback(null, selectedfilterOptions);
    setFilterSortIndicatorData({
      showFilterIndicator: selectedfilterOptions?.length > 0,
      showSortIndicator: false,
    });
  }, [selectedfilterOptions]);

  useEffect(() => {
    MerakiSDK.getSortFilters().then((_sortFilter: any) => {
      const _updatedSortFilter: any = {};
      if (showItemSort) {
        _updatedSortFilter["sort_by"] = _sortFilter["sort_by"];
      }
      if (showItemFilter) {
        _updatedSortFilter["filters"] = _sortFilter["filters"];
      }
      setSortFilter(_updatedSortFilter);
    });

    if (isUserAuthenticated) {
      MerakiSDK.getActiveOrders(1, 1).then((orders: any) => {
        if (orders.length > 0) {
          setActiveOrder(orders[0]);
        }
      });
    }
  }, [showItemSort, showItemFilter, isUserAuthenticated]);

  // LCP Optimization: Banners are already fetched in getStaticProps (SSG/ISR) and passed as props.
  useEffect(() => {
    // Skip on initial mount — SSG banners are already rendered (LCP optimization)
    if (initialMount.current) {
      initialMount.current = false;
      return;
    }

    if (!selectedStore?.id) return;

    setIsLoading(true);
    getBanners(defaultBanners)
      .then(({ webBanner, mobileBanner }) => {
        setWebBanner(webBanner);
        setMobileBanner(mobileBanner);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [selectedStore?.id]);

  const repopulateCategories = useCallback(
    (
      selectedSortOptions: any,
      selectedfilterOptions: any,
      isHeaderEvent = false,
    ) => {
      // setCategoriesLoading(true);
      MerakiSDK.getCategories({
        recommendedItems,
        sort_by: selectedSortOptions,
        filter_by: selectedfilterOptions,
      })
        .then((_categories: any) => {
          setAllCategories(_categories);
          if (!_categories.length) {
            setItemsMap({});
          }
          if (!isHeaderEvent)
            setFilterSortIndicatorData({
              showFilterIndicator: selectedfilterOptions?.length > 0,
              showSortIndicator: selectedSortOptions?.length > 0,
            });

          return _categories;
        })
        .then((_categories: any) => {
          return getFullCatalogue(
            _categories,
            selectedSortOptions,
            selectedfilterOptions,
          );
        })
        .then((categoryItemMap: any) => {
          setItemsMap(categoryItemMap);
        })
        .finally(() => {
          setCategoriesLoading(false);
        });
    },
    [recommendedItems, language],
  );

  // Extract query params as stable primitive values for dependency tracking
  const fulfillmentModeParam = router.query.fulfillment_mode as
    | string
    | undefined;
  const locationIdParam = router.query.location_id as string | undefined;

  // Effect 1: Sync store selection from URL query params
  useEffect(() => {
    if (!router.isReady || !fulfillmentModeParam || !locationIdParam) return;

    const locationId = Number(locationIdParam);
    const enabledFulfillmentModes = configSelector.getFulfilmentTypes();

    if (!enabledFulfillmentModes.includes(fulfillmentModeParam)) return;

    // Skip if the store already matches the query params
    if (
      selectedStore?.id === locationId &&
      selectedStore?.ffMode === fulfillmentModeParam
    )
      return;

    MerakiSDK.getStoreFromLocationId(locationId).then((store: any) => {
      if (store && store?.enabled_for_ordering) {
        MerakiSDK.setSelectedStore(
          {
            id: store.biz_location_id,
            address: fulfillmentModeParam === "pickup" ? store.address : "",
            latitude: store.lat,
            longitude: store.lng,
            storeInfo: store,
            ffMode: fulfillmentModeParam as FulfillmentMode,
            placeId: store.place_id || "",
          },
          true,
        );

        // Clean consumed query params from the URL
        const { fulfillment_mode, location_id, ...restQuery } = router.query;
        router.replace(
          {
            pathname: router.pathname,
            query: restQuery,
          },
          undefined,
          { shallow: true },
        );

        if (fulfillment_mode === "delivery") {
          PubSub.getInstance().emit(EVENTS.STORE_SELECTION_REQUIRED);
        }
      }
    });
  }, [fulfillmentModeParam, locationIdParam, router.isReady]);

  // Effect 2: Reset filters and repopulate categories when the store changes
  useEffect(() => {
    if (mandatoryLocationSelection && !selectedStore?.id) {
      return;
    }
    setSelectedSortOptions(null);
    setSelectedfilterOptions([]);
    repopulateCategories(null, []);
  }, [
    selectedStore?.id,
    selectedStore?.ffMode,
    repopulateCategories,
    mandatoryLocationSelection,
  ]);

  useEffect(() => {
    const filterCallbackListener = PubSub.getInstance().addListener(
      EVENTS.APPLY_FILTER_CALLBACK,
      (args: any) => {
        const { selectedSortOptions, selectedfilterOptions } = args;
        onFilterByCallback(selectedSortOptions, selectedfilterOptions, true);
      },
    );

    return () => {
      PubSub.getInstance().removeListener(filterCallbackListener);
    };
  }, []);

  const onCategoryChange = async (category: any) => {
    const categorySlug = category?.slug;

    if (categorySlug) {
      router.push(`/category/${category.id}/${categorySlug}`, "", {
        scroll: false,
      });
    }

    try {
      MerakiSDK.analyticsPublish(ANALYTICS_EVENTS.CATEGORY_DETAIL, {
        ...category,
      });
    } catch (error) {
      console.error(
        "AnalyticsPublish: ",
        ANALYTICS_EVENTS.CATEGORY_DETAIL,
        error,
      );
    }
  };

  const toggleFavourite = async (item: any, status: boolean) => {
    if (status) {
      await MerakiSDK.addItemAsFavourite(item.id);
    } else {
      await MerakiSDK.removeItemAsFavourite(item.id);
    }
  };

  const onError = (err: Error, item: any) => {
    if (err.message === Exceptions.LOGIN_REQUIRED) {
      PubSub.getInstance().emit(EVENTS.LOGIN_REQUIRED);
    }
    if (err.message === Exceptions.STORE_SELECTION_REQUIRED) {
      PubSub.getInstance().emit(EVENTS.STORE_SELECTION_REQUIRED);
    }
  };

  const fetchCategoryItems = async (categoryIds: number[]) => {
    /**
     * TODO: We will need a config in root config which will decide whether to fetch all items
     * at once or once by once
     */
    // if (!categoryIds?.length) return;
    // // get unfetched categories
    // const unfetchedCategories = categoryIds.filter(
    //   (id: number) => !itemsMap[id] && !isNaN(id)
    // );
    // if (!unfetchedCategories?.length) return;
    // setItemsMap((itemsMap: any) => ({
    //   ...itemsMap,
    //   ...unfetchedCategories.reduce(
    //     (result, category) => ({
    //       ...result,
    //       [category]: [],
    //     }),
    //     {}
    //   ),
    // }));
    // const categoryItems = await MerakiSDK.getCategoryItems({
    //   categoryIds: unfetchedCategories,
    //   sort_by: selectedSortOptions,
    //   filter_by: selectedfilterOptions,
    // });
    // const categoryItemMap = categoryItems?.reduce((acc: any, category: any) => {
    //   acc[category.id] = category.items;
    //   return acc;
    // }, {});
    // setItemsMap((itemsMap: any) => ({
    //   ...itemsMap,
    //   ...categoryItemMap,
    // }));
  };

  const getBannerStyles = (): string => {
    let bannerStyle = styles.menuCarousel;

    if (isMobile) {
      bannerStyle += _mobileBanner?.length ? "" : "--no-banner";
    } else {
      bannerStyle += _webBanner?.length
        ? _webBanner.length < 3
          ? "--single-banner"
          : "--multiple-banners"
        : "--no-banner";
    }
    return bannerStyle;
  };

  const renderEmptyMenuState = () => {
    return (
      <div className={styles.emptyMenuContainer}>
        <LocationSearch />
        <Typography variant="h1" weight="bold" classes={styles.emptyMenuTitle}>
          {t("common.startOrderingPickLocationTitle")}
        </Typography>
        <Typography classes={styles.emptyMenuDesc} variant="bodyTextOne">
          {t("common.startOrderingPickLocationDesc")}
        </Typography>
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <div className={`${styles.menu} container`}>
        <Head>
          <title>{seoConfig?.title}</title>
          <meta name="description" content={seoConfig?.description} />
          <meta name="keywords" content={seoConfig?.keywords} />
        </Head>
        <h1 className="visually-hidden">{seoConfig?.title || "Menu"}</h1>
        {mandatoryLocationSelection &&
        !selectedStore?.storeInfo?.biz_location_id ? (
          renderEmptyMenuState()
        ) : (
          <React.Fragment>
            <div>
              {/* {isLoading ? ( */}
              <div
                style={isLoading ? { display: "block" } : { display: "none" }}
              >
                {themeName === "luna" ? (
                  <CarouselMultiShimmer />
                ) : (
                  <CarouselWideShimmer />
                )}
              </div>
              {/* ) : ( */}
              <div
                style={isLoading ? { display: "none" } : { display: "block" }}
              >
                {_mobileBanner?.length || _webBanner?.length ? (
                  <div
                    style={{ minHeight: "216px" }}
                    className={getBannerStyles()}
                  >
                    {isMobile ? (
                      _mobileBanner?.length ? (
                        <div className={styles.carouselMobile}>
                          <CarouselMobile
                            bannerLayout={bannerLayout}
                            slides={_mobileBanner}
                            imageComponent={LazyImage}
                          />
                        </div>
                      ) : null
                    ) : _webBanner?.length ? (
                      <div
                        style={{ height: "750px" }}
                        className={styles.carouselDesktop}
                      >
                        <CarouselDesktop
                          bannerLayout={bannerLayout}
                          slides={_webBanner}
                          imageComponent={LazyImage}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
              {/* )} */}
            </div>

            <div className={styles.itemList}>
              <MenuPanel
                theme={themeName}
                loading={categoriesLoading}
                isFavouriteEnabled={likeButton}
                showFoodType={showFoodType}
                minOrder={
                  selectedStore?.storeInfo?.min_order_total ||
                  businessInfo?.min_order_total ||
                  0
                }
                selectedCategory={
                  categoryId === null ? null : Number(categoryId)
                }
                sortFilter={sortFilter}
                categories={allCategories}
                itemsMap={itemsMap}
                onCategoryClick={onCategoryChange}
                imageComponent={LazyImage}
                fetchCategoryItems={fetchCategoryItems}
                onItemUpdate={onItemUpdate}
                cartItems={cartItems}
                totalItems={totalItems}
                subTotal={subTotal}
                onError={onError}
                onItemClicked={(item: any) => {
                  sessionStorage.setItem(
                    "scrolledSoFar",
                    String(window.pageYOffset),
                  );
                  router.push(`/item/${item.id}/${item.slug}`);
                }}
                onCheckoutClicked={async () => {
                  await router.push("/checkout");
                }}
                toggleFavourite={toggleFavourite}
                onFilterByCallback={onFilterByCallback}
                onFilterByChange={onFilterByChange}
                onSortByChange={onSortByChange}
                onFilterClearCallback={onFilterClearCallback}
                onSortClearCallback={onSortClearCallback}
                selectedfilterOptions={selectedfilterOptions}
                selectedSortOptions={selectedSortOptions}
                editCustomizationCallback={onItemCustomization}
                filterSortIndicatorData={filterSortIndicatorData}
                itemLevelInstructionsEnabled={
                  businessInfo?.allow_item_level_notes ?? true
                }
              />
            </div>
          </React.Fragment>
        )}

        {isMobile && totalItems > 0 && (
          <FloatingStack order={2}>
            <FloatingCart
              item_count={totalItems}
              minOrder={
                selectedStore?.storeInfo?.min_order_total ||
                businessInfo?.min_order_total ||
                0
              }
              sub_total={subTotal}
              onClick={() => router.push("/checkout")}
            />
          </FloatingStack>
        )}

        {activeOrder && !totalItems ? (
          <FloatingStack order={1}>
            <FloatingStatus
              variant="order"
              onCtaClick={() => router.push(`/orders/${activeOrder.id}`)}
            />
          </FloatingStack>
        ) : null}
      </div>
    </div>
  );
}

export default MenuPage;
